import { Injectable } from "@angular/core";

interface Node {
    type: string;
    [key: string]: any;
}

@Injectable({
    providedIn: "root"
})
export class ExpressionEvaluatorService {
    private readonly operators = new Set(["||", "&&", ">", "<", ">=", "<=", "==", "===", "!=", "!=="]);

    constructor() {}

    evaluate(expression: string, context: any): any {
        try {
            this.validateContext(context);
            const tokens = this.tokenize(expression);
            this.validateTokens(tokens);
            const ast = this.parse(tokens);
            const res = this.evaluateNode(ast, context);
            return res;
        } catch (error) {
            console.error("Error evaluating expression:", error);
            return false;
        }
    }

    private validateContext(context: any): void {
        for (const key in context) {
            if (!/^[A-Za-z0-9_]+$/.test(key)) {
                throw new Error(`Invalid context identifier: ${key}`);
            }
        }
    }

    private tokenize(expression: string): string[] {
        const regex = /\s*(['"])((?:\\.|[^\\])*?)\1|([A-Za-z0-9_]+)|(\.includes|\(|\)|&&|\|\||[><=!]=?)\s*/g;
        const matches = [];
        let match;
        while ((match = regex.exec(expression)) !== null) {
            if (match[1]) {
                matches.push(`${match[1]}${match[2]}${match[1]}`);
            } else if (match[3]) {
                matches.push(match[3]);
            } else if (match[4]) {
                matches.push(match[4]);
            }
        }
        return matches;
    }

    private validateTokens(tokens: string[]): void {
        for (const token of tokens) {
            if (
                !/^['"](?:\\.|[^\\])*['"]$/.test(token) && // Strings
                !/^[A-Za-z0-9_]+$/.test(token) && // Identifier
                !/^\.\w+$/.test(token) && // Dot + method name
                !this.operators.has(token) && // Operator
                token !== "(" &&
                token !== ")"
            ) {
                throw new Error(`Invalid token: ${token}`);
            }
        }
    }

    private parse(tokens: string[]): Node {
        const stack = [];
        const output = [];
        const operators = {
            "||": 1,
            "&&": 2,
            ">": 3,
            "<": 3,
            ">=": 3,
            "<=": 3,
            "==": 3,
            "===": 3,
            "!=": 3,
            "!==": 3
        };

        for (let i = 0; i < tokens.length; i++) {
            const token = tokens[i];
            if (/^['"].*['"]$/.test(token)) {
                output.push({ type: "Literal", value: token.slice(1, -1) });
            } else if (/^\d+(\.\d+)?$/.test(token)) {
                output.push({ type: "Literal", value: parseFloat(token) });
            } else if (/^[A-Za-z0-9_]+$/.test(token)) {
                output.push({ type: "Identifier", name: token });
            } else if (token in operators) {
                while (stack.length && operators[stack[stack.length - 1]] >= operators[token]) {
                    output.push({ type: "Operator", value: stack.pop() });
                }
                stack.push(token);
            } else if (token === "(") {
                stack.push(token);
            } else if (token === ")") {
                while (stack.length && stack[stack.length - 1] !== "(") {
                    output.push({ type: "Operator", value: stack.pop() });
                }
                stack.pop();
            } else if (token === ".includes") {
                // Processando a chamada de método .includes
                const arrayToken = output.pop();
                const nextToken = tokens[++i];
                if (!arrayToken || nextToken !== "(") {
                    throw new Error("Invalid includes syntax");
                }
                const argToken = tokens[++i];
                if (!argToken || !/^['"].*['"]$/.test(argToken)) {
                    throw new Error("Invalid includes syntax");
                }
                const closingParenToken = tokens[++i];
                if (closingParenToken !== ")") {
                    throw new Error("Invalid includes syntax");
                }
                output.push({
                    type: "CallExpression",
                    callee: {
                        type: "MemberExpression",
                        object: arrayToken,
                        property: { type: "Identifier", name: "includes" }
                    },
                    arguments: [{ type: "Literal", value: argToken.slice(1, -1) }]
                });
            }
        }

        while (stack.length) {
            output.push({ type: "Operator", value: stack.pop() });
        }

        return this.buildAST(output);
    }

    private buildAST(tokens: Node[]): Node {
        const stack = [];

        for (const token of tokens) {
            if (token.type === "Literal" || token.type === "Identifier") {
                stack.push(token);
            } else if (token.type === "Operator") {
                const right = stack.pop();
                const left = stack.pop();
                stack.push({
                    type: "BinaryExpression",
                    operator: token.value,
                    left,
                    right
                });
            } else if (token.type === "CallExpression") {
                stack.push(token);
            }
        }

        return stack[0];
    }

    private evaluateNode(node: Node, context: any): any {
        switch (node.type) {
            case "Literal":
                return node.value;
            case "Identifier":
                return context[node.name];
            case "BinaryExpression":
                return this.evaluateBinaryExpression(node, context);
            case "CallExpression":
                return this.evaluateCallExpression(node, context);
            default:
                throw new Error(`Unknown node type: ${node.type}`);
        }
    }

    private evaluateBinaryExpression(node: Node, context: any): any {
        const left = this.evaluateNode(node.left, context);
        const right = this.evaluateNode(node.right, context);

        switch (node.operator) {
            case ">":
                return left > right;
            case "<":
                return left < right;
            case ">=":
                return left >= right;
            case "<=":
                return left <= right;
            case "==":
                return left == right;
            case "===":
                return left === right;
            case "!=":
                return left != right;
            case "!==":
                return left !== right;
            case "||":
                return left || right;
            case "&&":
                return left && right;
            default:
                throw new Error(`Unsupported operator: ${node.operator}`);
        }
    }

    private evaluateCallExpression(node: Node, context: any): any {
        const object = this.evaluateNode(node.callee.object, context);
        const args = node.arguments.map((arg: Node) => this.evaluateNode(arg, context));

        if (!Array.isArray(object) || object.length === 0) {
            // Se não for um array ou estiver vazio, retorna false
            return false;
        }

        if (node.callee.property.name === "includes") {
            return object.includes(args[0]);
        } else {
            throw new Error(`Unsupported function: ${node.callee.property.name}`);
        }
    }
}
